// 3rd party
import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

// Components
import Spinner from "../../components/spinner/Spinner";
import StatelevelFormDialog from "./StatelevelFormDialog";

// Services
import {
  AdminEntry,
  defaultAdminEntry,
  prepareAdminEntryForEdit,
} from "../../services/StatelevelService";
import { logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// Config
import { appConfig } from "../../config/appConfig";

// Asset components
import visiable from "../../assets/visiable.svg";
import edit from "../../assets/edit.svg";
import delete1 from "../../assets/delete.svg";
import add from "../../assets/add.svg";
import approve from "../../assets/approve.svg";

// Styles
import "./Statelevel.scss";

interface Catgry {
  id: number;
  name: string;
}

interface State {
  id: number;
  name: string;
}
const Statelevel = () => {
  // useRoutes
  const navigate = useNavigate();

  // useState
  const [dataList, setDataList] = useState<AdminEntry[]>([]);
  const [filterList, setFilterList] = useState<AdminEntry[]>([]);
  const [filterCategory, setfilterCategory] = useState<string | null>(null);
  const [filterState, setfilterState] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [categories, setCategories] = useState<Catgry[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [formData, setFormData] = useState<AdminEntry>(defaultAdminEntry);
  const [page, setPage] = useState(0);
  const [role, setRole] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const rowsPerPage = 7;

  // useEffect - Load role from local storage
  useEffect(() => {
    const storedRole = getStorageItemLocal("role");
    if (storedRole) setRole(JSON.parse(storedRole));
  }, []);

  // useEffect - Fetch categories and states
  useEffect(() => {
    const fetchStates = async () => {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      try {
        const response = await fetch(
          `${appConfig.serviceUrls.getAdminEntries}/states`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch states");
        const data = await response.json();
        setStates(data);
      } catch (error) {
        console.error("Error fetching states", error);
      }
    };

    const fetchCategories = async () => {
      try {
        const tokenString = getStorageItemLocal("token");
        const token = tokenString ? JSON.parse(tokenString) : null;
        const response = await fetch(
          `${appConfig.serviceUrls.getAdminEntries}/categories`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch categories");
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };

    fetchCategories();
    fetchStates();
  }, []);

  // useEffect - Load data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem("adminDataList");
    if (savedData) {
      const parsedData: AdminEntry[] = JSON.parse(savedData);
      setDataList(parsedData);
      setFilterList(parsedData);
      if (!savedData || parsedData.length === 0) {
        setEditingIndex(null);
      }
    }
  }, []);

  // useEffect - Filter data when filters change
  useEffect(() => {
    const filteredData = filterList.filter((item) => {
      const matchesState =
        !filterState ||
        (item.stateName || "")
          .trim()
          .toLowerCase()
          .includes(filterState.trim().toLowerCase());

      const matchesCategory =
        !filterCategory ||
        (item.categoryName || "")
          .trim()
          .toLowerCase()
          .includes(filterCategory.trim().toLowerCase());

      return matchesState && matchesCategory;
    });

    setDataList(filteredData);
  }, [filterCategory, filterState]);

  // useEffect - Fetch latest data from server
  useEffect(() => {
    fetchData();
  }, []);

  // Removed rogue useEffect that was overwriting formData during edit mode
  // Methods

  const fetchData = async () => {
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(appConfig.serviceUrls.saveStateEntries, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch data");
      const data: AdminEntry[] = await response.json();

      setDataList(data || []);
      setFilterList(data || []);
      import("../../services/ExtraCategoriesSyncService")
        .then(({ syncExtraCategoriesToBlob }) => syncExtraCategoriesToBlob())
        .catch(() => {});
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Network Error");
    }
  };

  const resetForm = () => {
    setFormData({
      ...defaultAdminEntry,
      id: dataList.length + 1, // Ensure a new ID
      lastUpdated: new Date().toISOString(), // Update timestamp
    });
    setEditingIndex(null);
  };
  //Add New Record

  const handleAdd = () => {
    setEditingIndex(null);
    setShowForm(true);
    setFormData({
      ...defaultAdminEntry,
      id: 0, // You can also use dataList.length + 1 if needed
      lastUpdated: new Date().toISOString(),
    });
  };

  // handle view
  const handleView = (index: number) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }

    const preparedEntry = prepareAdminEntryForEdit(selectedEntry);

    setFormData(preparedEntry);
    setEditingIndex(-1);
    setShowForm(true);
  };
  const handleEdit = (index: number) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }

    const preparedEntry = prepareAdminEntryForEdit(selectedEntry);

    setFormData(preparedEntry);
    setEditingIndex(preparedEntry.id);
    setShowForm(true);
  };

  //Delete a record
  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure want to delete this entry?")) return;
    setLoading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(
        `${appConfig.serviceUrls.saveStateEntries}/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }

      if (!response.ok) throw new Error("Failed to delete");

      import("../../services/ExtraCategoriesSyncService")
        .then(({ syncExtraCategoriesToBlob }) => syncExtraCategoriesToBlob())
        .catch(() => {});

      fetchData();
      if (editingIndex !== null && dataList[editingIndex]?.id === id) {
        setEditingIndex(null);
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (index: number) => {
    if (!window.confirm("Are you sure want to publish this entry?")) return;

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }

    const formDataToSend = new FormData();

    const appendBasic = (key: string, value: any) => {
      if (value !== undefined && value !== null)
        formDataToSend.append(key, String(value));
    };

    // Basic fields
    appendBasic("id", selectedEntry.id);
    appendBasic("category", selectedEntry.category);
    appendBasic("state", selectedEntry.state);
    appendBasic("description", selectedEntry.description);
    appendBasic("description2", selectedEntry.description2);
    appendBasic("description3", selectedEntry.description3);
    appendBasic("pdfIsActive", selectedEntry.pdfIsActive);
    appendBasic("pdfName", selectedEntry.pdfName);
    appendBasic("pdfIsActive2", selectedEntry.pdfIsActive2);
    appendBasic("pdfName2", selectedEntry.pdfName2);
    appendBasic("pdfIsActive3", selectedEntry.pdfIsActive3);
    appendBasic("pdfName3", selectedEntry.pdfName3);
    appendBasic("isActive", selectedEntry.isActive);
    appendBasic("uploadBy", selectedEntry.uploadBy);
    appendBasic("approveBy", selectedEntry.approveBy);
    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("publishBy", username);
    appendBasic("status", "Published"); // override
    appendBasic("statusCode", 3);

    // Grid fields (loop 1–9)
    for (let i = 1; i <= 9; i++) {
      const key = `gridType${i}` as keyof AdminEntry;
      appendBasic(key, selectedEntry[key]);

      const key1 = `gridIconName${i}` as keyof AdminEntry;
      appendBasic(key1, selectedEntry[key1]);

      const key2 = `grid${i}` as keyof AdminEntry;
      appendBasic(key2, selectedEntry[key2]);

      const key3 = `gridThumbnail${i}` as keyof AdminEntry;
      appendBasic(key3, selectedEntry[key3]);
    }

    const url = `${appConfig.serviceUrls.saveStateEntries}/${selectedEntry.id}`;

    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },

        body: formDataToSend,
      });
      if (response.status === 401) {
        logout(navigate);
        return;
      }
      if (!response.ok) throw new Error("Failed to save data");

      await import("../../services/ExtraCategoriesSyncService")
        .then(({ syncExtraCategoriesToBlob }) => syncExtraCategoriesToBlob())
        .catch(() => {});

      alert("Data published successfully");
      fetchData();
      resetForm();
      setEditingIndex(null);
    } catch (error) {
      console.error("Error publishing data:", error);
      alert("Error publishing data");
    }
  };

  return (
    <>
      <Box className="container-wraper">
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              paddingBottom: "5px",
            }}
          >
            <Typography className="bold-header-text">
              <b>State Level Data</b>{" "}
            </Typography>
            <Button onClick={handleAdd} size="small" className="custom-button">
              <img src={add} alt="visiable" className="img-style" />
              {"   "}
              <Typography className="btn-text"> {"   "}Add</Typography>
            </Button>
          </Box>
        </>

        <Box
          display="flex"
          gap={2}
          alignItems="center"
          justifyContent="start"
          mb={2}
        >
          <TextField
            select
            label="State"
            name="state"
            size="small"
            value={filterState}
            onChange={(e) => setfilterState(e.target.value)}
            sx={{ width: "230px", height: "40px", background: "#fff" }}
          >
            <MenuItem value="">All</MenuItem>
            {states.map((st: State) => (
              <MenuItem key={st.id} value={st.name}>
                {st.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Category"
            size="small"
            value={filterCategory}
            onChange={(e) => setfilterCategory(e.target.value)}
            sx={{ width: "230px", height: "40px", background: "#fff" }}
          >
            <MenuItem value="">All Category</MenuItem>
            {categories.map((cat: Catgry) => (
              <MenuItem key={cat.id} value={cat.name}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          {/*Data Table*/}
          {loading ? (
            <>
              {" "}
              <Spinner></Spinner>
            </>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                width: "100%",
              }}
            >
              {dataList?.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow className="custom-table-head-row">
                      <TableCell>
                        <b>State</b>
                      </TableCell>
                      <TableCell>
                        <b>Category</b>
                      </TableCell>

                      <TableCell>
                        <b>Media /Text / PDF</b>
                      </TableCell>
                      <TableCell>
                        <b>Last Update</b>
                      </TableCell>

                      <TableCell className="nowrap">
                        <b>Status</b>
                      </TableCell>
                      <TableCell>
                        <b>Actions</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataList
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((item, index) => (
                        <TableRow key={index} className="custom-table-row">
                          <TableCell className="nowrap">
                            {item.stateName || "\u00A0"}
                          </TableCell>

                          <TableCell className="nowrap">
                            {item.categoryName || "\u00A0"}
                          </TableCell>

                          <TableCell className="common-txt nowrap">
                            <Box className="description-wraper">
                              <Typography className="description-txt">
                                {" "}
                                View
                              </Typography>{" "}
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  handleView(page * rowsPerPage + index);
                                }}
                              >
                                <img
                                  src={visiable}
                                  alt="visible"
                                  className="img-style"
                                />
                              </IconButton>
                            </Box>
                          </TableCell>

                          <TableCell className="nowrap formatted-date">
                            {item.lastUpdated
                              ? new Date(item.lastUpdated).toLocaleString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  }
                                )
                              : "\u00A0"}
                          </TableCell>

                          {item.status === "Approved" ? (
                            <TableCell className="approved-text">
                              {item.status}
                            </TableCell>
                          ) : item.status === "Published" ? (
                            <TableCell className="approved-text">
                              {item.status}
                            </TableCell>
                          ) : item.status === "Pending" ? (
                            <TableCell className="pending-txt">
                              {item.status}
                            </TableCell>
                          ) : (
                            <TableCell className="publish-txt">
                              {item.status}
                            </TableCell>
                          )}
                          <TableCell
                            align="left"
                            className="nowrap sticky-right-top"
                          >
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => {
                                handleEdit(page * rowsPerPage + index);
                              }}
                            >
                              <img
                                src={edit}
                                alt="visiable"
                                className="img-style"
                              />
                            </IconButton>
                            {role === "System Admin" && (
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleDelete(item.id)}
                              >
                                <img
                                  src={delete1}
                                  alt="visiable"
                                  className="img-style"
                                />
                              </IconButton>
                            )}

                            {item.status === "Approved" && (
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() =>
                                  handlePublish(page * rowsPerPage + index)
                                }
                              >
                                <img
                                  src={approve}
                                  alt="visiable"
                                  className="img-style"
                                  // onClick={() => alert("function not implemented")}
                                />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <Box
                  textAlign="center"
                  mt={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "1rem",
                  }}
                >
                  <Typography align="center" sx={{ mt: 2 }}>
                    No records found.
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={handleAdd}
                  >
                    <Add fontSize="small" />
                  </Button>
                </Box>
              )}
              {loading ? (
                <>
                  {" "}
                  <Spinner></Spinner>
                </>
              ) : (
                <></>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",

                  bottom: 0,

                  zIndex: 1,

                  overflowX: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#000",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "normal",
                  }}
                >
                  Showing {page * rowsPerPage + 1} to{" "}
                  {Math.min(dataList.length, page * rowsPerPage + rowsPerPage)}{" "}
                  of {dataList.length} entries
                </Typography>
                <Pagination
                  count={Math.ceil(dataList.length / rowsPerPage)}
                  page={page + 1}
                  onChange={(e, value) => setPage(value - 1)}
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                />
              </Box>
            </TableContainer>
          )}
        </Box>
        <Box>
          <StatelevelFormDialog
            open={showForm}
            // onClose={handleCloseDialog}
            fetchData={fetchData}
            resetForm={resetForm}
            categories={categories}
            states={states}
            formData={formData}
            setFormData={setFormData}
            editingIndex={editingIndex}
            setEditingIndex={setEditingIndex}
            setShowForm={setShowForm}
            dataList={dataList}
          />
        </Box>
      </Box>
    </>
  );
};

export default Statelevel;
